// An error that offers the way out of itself.
//
// A refusal whose remedy lives on another screen — "Add the airport to your
// served list before pricing it" — is correct and still leaves the user to
// find that list, realise it is a separate save, do it, come back, and retype
// what they lost. The message names the prerequisite; it does nothing about
// it.
//
// So when the API sends a `fix` (see ErrorFix in @drivecars/shared), this
// renders it as a link beside the message.
//
// ROUTES ARE NOT KNOWN HERE. This package must stay app-agnostic — the same
// prerequisite lives at different paths in the crew app and the backoffice,
// and an ops fix may target a specific crew. So the caller passes `resolve`,
// which turns the API's abstract action into one of ITS routes. An action the
// app has nowhere to send the user resolves to null and the message renders
// alone rather than as a link that goes somewhere wrong.

import { useEffect, useRef, type ReactNode } from 'react';
import type { ErrorFix } from '@drivecars/shared';

export interface ErrorNoticeProps {
  /** The human-readable failure. Nothing renders without it. */
  message: string | null | undefined;
  /** The remedy, when the API named one. */
  fix?: ErrorFix | null;
  /**
   * Maps an ErrorFix to a href in THIS app, or null when this app has nowhere
   * to send the user for it.
   */
  resolve?: (fix: ErrorFix) => string | null;
  /**
   * Renders the link. Passed in so the package stays free of any router —
   * both apps use TanStack Router, but importing it here would tie this
   * package to their route trees.
   */
  linkRenderer?: (href: string, label: string) => ReactNode;
}

export function ErrorNotice({ message, fix, resolve, linkRenderer }: ErrorNoticeProps) {
  const ref = useRef<HTMLDivElement>(null);

  // BRING THE ERROR INTO VIEW.
  //
  // These screens are long — the transfers grid runs well past a viewport —
  // and the error banner sits at the top. Press Save at the bottom and the
  // refusal renders somewhere you cannot see: the button appears to do
  // nothing, and the explanation (and now the link that fixes it) is scrolled
  // off. Only scrolls when it is actually out of view, so an error already on
  // screen does not yank the page around.
  useEffect(() => {
    if (!message) return;
    const el = ref.current;
    if (!el) return;
    const { top, bottom } = el.getBoundingClientRect();
    const visible = top >= 0 && bottom <= window.innerHeight;
    if (!visible) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // `message` in the deps, not just presence: a SECOND failure with
    // different text is new information and deserves the same treatment.
  }, [message]);

  if (!message) return null;
  const href = fix && resolve ? resolve(fix) : null;

  return (
    <div
      ref={ref}
      // role=alert: this replaces a message the user was already reading as an
      // error, and a silent swap leaves a screen reader on the old one.
      role="alert"
      className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-800"
    >
      {message}
      {href && linkRenderer ? (
        <span className="ms-2 font-medium underline underline-offset-2">
          {linkRenderer(href, fix!.label)}
        </span>
      ) : null}
    </div>
  );
}
